import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Copy, ExternalLink, Globe } from "lucide-react";
import { toast } from "sonner";
import { API, BACKEND } from "@/lib/api";


const inputCls =
    "rounded-md border border-slate-800 bg-slate-950/70 px-3.5 py-2 font-mono text-xs text-slate-200 placeholder:text-slate-600 focus:border-cyan-500/60 focus:outline-none";

export default function PortalTab() {
    const [apps, setApps] = useState(null);
    const [selected, setSelected] = useState("");
    const [enabled, setEnabled] = useState(false);
    const [slug, setSlug] = useState("");
    const [brand, setBrand] = useState("");
    const [color, setColor] = useState("#06B6D4");
    const [copied, setCopied] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const r = await fetch(`${API}/apps`, { credentials: "include" });
                if (r.ok) {
                    const d = await r.json();
                    setApps((d.apps || []).filter((a) => a.template === "outbound-webhooks"));
                }
            } catch {
                setApps([]);
            }
        })();
    }, []);

    useEffect(() => {
        const app = (apps || []).find((a) => a.app_id === selected);
        const cfg = app?.config || {};
        setEnabled(!!cfg.portal_enabled);
        setSlug(cfg.portal_slug || "");
        setBrand(cfg.portal_brand || app?.name || "");
        setColor(cfg.portal_color || "#06B6D4");
    }, [selected, apps]);

    const save = async () => {
        setSaving(true);
        try {
            const r = await fetch(`${API}/apps/${selected}/config`, {
                method: "PATCH",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    config: {
                        portal_enabled: enabled,
                        portal_slug: slug.trim(),
                        portal_brand: brand.trim(),
                        portal_color: color,
                    },
                }),
            });
            const d = await r.json().catch(() => ({}));
            if (r.status === 409) throw new Error(d.detail || "slug taken");
            if (!r.ok) throw new Error("failed");
            setSlug(d.config.portal_slug || "");
            toast.success("Portal settings saved");
        } catch (e) {
            toast.error("Could not save", { description: String(e.message) });
        } finally {
            setSaving(false);
        }
    };

    const portalUrl = `${window.location.origin}/portal/${slug}`;

    const copyUrl = async () => {
        try {
            await navigator.clipboard.writeText(portalUrl);
            setCopied(true);
            toast.success("Portal URL copied");
            setTimeout(() => setCopied(false), 1500);
        } catch {
            toast.error("Copy failed");
        }
    };

    return (
        <div data-testid="settings-portal-tab" className="glass-panel rounded-xl p-6">
            <h3 className="flex items-center gap-2 font-display text-sm font-bold tracking-tight text-slate-100">
                <Globe className="h-4 w-4 text-cyan-400" />
                White-label customer portal
            </h3>
            <p className="mt-2 max-w-lg text-xs leading-relaxed text-slate-400">
                Give your customers a branded, read-only view of their webhook endpoints and
                delivery health — hosted by MDH under your own brand.
            </p>

            {apps === null ? (
                <p className="mt-6 font-mono text-xs text-slate-500">Loading apps…</p>
            ) : apps.length === 0 ? (
                <div className="mt-6 rounded-lg border border-dashed border-slate-800 py-8 text-center">
                    <p className="font-mono text-[11px] text-slate-500">
                        Portals attach to an Outbound Webhook Sender app.
                    </p>
                    <Link
                        to="/templates"
                        data-testid="portal-create-app-link"
                        className="mt-4 inline-block rounded-md bg-cyan-400 px-5 py-2.5 text-xs font-bold text-slate-950 transition-colors hover:bg-cyan-300"
                    >
                        Deploy the template
                    </Link>
                </div>
            ) : (
                <div className="mt-5 grid max-w-lg gap-3">
                    <label className="block">
                        <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                            App
                        </span>
                        <select
                            data-testid="portal-app-select"
                            value={selected}
                            onChange={(e) => setSelected(e.target.value)}
                            className={`${inputCls} w-full`}
                        >
                            <option value="">Choose an app…</option>
                            {apps.map((a) => (
                                <option key={a.app_id} value={a.app_id}>
                                    {a.name}
                                </option>
                            ))}
                        </select>
                    </label>

                    {selected && (
                        <>
                            <label className="flex items-center gap-2.5 font-mono text-[11px] text-slate-300">
                                <input
                                    data-testid="portal-enabled-toggle"
                                    type="checkbox"
                                    checked={enabled}
                                    onChange={(e) => setEnabled(e.target.checked)}
                                    className="h-3.5 w-3.5 accent-cyan-400"
                                />
                                Portal is public
                            </label>
                            <div className="grid gap-3 sm:grid-cols-2">
                                <label className="block">
                                    <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                        Brand name
                                    </span>
                                    <input
                                        data-testid="portal-brand-input"
                                        value={brand}
                                        onChange={(e) => setBrand(e.target.value)}
                                        className={`${inputCls} w-full`}
                                    />
                                </label>
                                <label className="block">
                                    <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                        URL slug
                                    </span>
                                    <input
                                        data-testid="portal-slug-input"
                                        value={slug}
                                        onChange={(e) => setSlug(e.target.value)}
                                        placeholder="acme"
                                        className={`${inputCls} w-full`}
                                    />
                                </label>
                            </div>
                            <label className="block w-fit">
                                <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                    Accent color
                                </span>
                                <input
                                    data-testid="portal-color-input"
                                    type="color"
                                    value={color}
                                    onChange={(e) => setColor(e.target.value)}
                                    className="h-9 w-16 cursor-pointer rounded-md border border-slate-800 bg-slate-950/70 p-1"
                                />
                            </label>
                            <div className="flex flex-wrap items-center gap-2">
                                <button
                                    data-testid="portal-save-button"
                                    onClick={save}
                                    disabled={saving || !slug.trim()}
                                    className="rounded-md bg-cyan-400 px-5 py-2.5 text-xs font-bold text-slate-950 transition-colors hover:bg-cyan-300 disabled:opacity-50"
                                >
                                    {saving ? "Saving…" : "Save portal"}
                                </button>
                                {enabled && slug && (
                                    <>
                                        <a
                                            data-testid="portal-open-link"
                                            href={portalUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="flex items-center gap-1.5 rounded-md border border-cyan-500/40 bg-cyan-950/40 px-3.5 py-2 font-mono text-[11px] text-cyan-300 hover:bg-cyan-950/70"
                                        >
                                            <ExternalLink className="h-3 w-3" /> Open portal
                                        </a>
                                        <button
                                            data-testid="portal-copy-button"
                                            onClick={copyUrl}
                                            className="flex items-center gap-1.5 rounded-md border border-slate-700 px-3.5 py-2 font-mono text-[11px] text-slate-400 hover:border-cyan-500/40 hover:text-cyan-300"
                                        >
                                            {copied ? (
                                                <Check className="h-3 w-3 text-emerald-400" />
                                            ) : (
                                                <Copy className="h-3 w-3" />
                                            )}
                                            {copied ? "copied" : "copy URL"}
                                        </button>
                                    </>
                                )}
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
